import { ComponentProps } from "react";
import { useWindowSize } from "@hooks/useWindowSize";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

type CardsProps = {
  /** The article to be displayed in the card */
  cardProps: ComponentProps<typeof Card>;
  /** Unique index of the article */
  index: number;
};

/**
 * Cards component to display a articles for top stories.
 * It renders a card with the desciption.
 * @figma https://www.figma.com/design/9RrH1KYIUOZk6rvgzlBBKX/WEB-BAU-2026?node-id=20436-71341&m=dev
 */
const Cards = ({ cardProps, index }: CardsProps): React.ReactElement | null => {
  const { isScreenLG } = useWindowSize();
  const isFourthArticle = index === 3;

  const variants: CardVariations = {
    content: {
      width: cn("w-[calc(100%-7.5rem)] lg:w-full", {
        "w-full lg:w-1/2 xl:w-1/3": isFourthArticle,
      }),
      extraClass: cn("h-auto mt-0 lg:mt-3 gap-1.5", {
        "lg:mt-0 lg:pr-4": isFourthArticle,
      }),
      blurb: {
        extraClass: cn(
          "font-lucida pr-4 text-base text-gray-750 max-lg:block font-medium first-letter:uppercase md:pr-0 -tracking-5% !line-clamp-5 leading-normal lg:!hidden",
          { "lg:!block": isFourthArticle }
        ),
      },
    },
    image: {
      position: isFourthArticle || !isScreenLG ? "right" : "left",
      width: cn("w-[7.5rem] lg:w-full", {
        "lg:w-1/2 xl:w-2/3": isFourthArticle,
      }),
      orientation: "landscape",
      extraClass: cn("h-auto"),
    },
    title: {
      size: cn("text-xl lg:text-lg", {
        "text-xl lg:text-4xl": isFourthArticle,
      }),
      extraClass: cn("mb-1.5 font-lct font-semibold -tracking-2% md:mb-0", {
        "lg:font-normal": isFourthArticle,
      }),
      color: "text-gray-815",
    },
    kicker: {
      color: "text-blue-175",
      extraClass: cn("text-base font-bold font-freightSansPro mb-1 md:mb-0"),
    },
    byline: {
      imageWidth: isScreenLG ? 60 : 48,
      imageHeight: isScreenLG ? 60 : 48,
      extraClass: "text-xs font-medium md:text-base",
    },
  };

  return (
    <Card
      {...cardProps}
      displayAsTopTitle={!isScreenLG}
      variations={variants}
      className={cn(
        "relative col-span-4 flex flex-row lg:flex-col",
        "relative lg:before:absolute lg:before:-right-4 lg:before:bottom-0 lg:before:top-0 lg:before:w-[1px] lg:before:bg-blue-100",
        "after:absolute after:-bottom-4 after:-left-0 after:-right-0 after:h-[1px] after:bg-blue-100 lg:after:-left-4 lg:after:-right-4",
        {
          "lg:after:left-0": index === 0,
          "lg:after:right-0": index === 2,
          "lg:col-span-8 lg:flex-row lg:after:hidden xl:col-span-9":
            isFourthArticle,
          "after:hidden lg:col-span-4 xl:col-span-3": index === 4,
          "lg:before:hidden": index === 4 || index == 2,
        }
      )}
    />
  );
};

export default Cards;
